import { ExtendedCoin, ExtendedVirtualCoin, ArkTransaction } from "../../wallet";
import { WalletRepository, WalletState, VtxoRepositoryKey } from "../walletRepository";
import {
    STORE_VTXOS,
    STORE_UTXOS,
    STORE_TRANSACTIONS,
    STORE_WALLET_STATE,
    serializeVtxo,
    serializeUtxo,
    deserializeVtxo,
    deserializeUtxo,
    SerializedVtxo,
    DB_VERSION,
} from "./db";
import { closeDatabase, openDatabase } from "./manager";
import { initDatabase } from "./schema";
import { scriptFromArkAddress } from "../scriptFromAddress";
import { DEFAULT_DB_NAME } from "../../worker/browser/utils";
import { isVtxoForScript } from "../../contracts/vtxoOwnership";

/**
 * IndexedDB-based implementation of WalletRepository.
 */
export class IndexedDBWalletRepository implements WalletRepository {
    readonly version = 1 as const;
    private db: IDBDatabase | null = null;

    constructor(private readonly dbName: string = DEFAULT_DB_NAME) {}

    async clear(): Promise<void> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(
                    [STORE_VTXOS, STORE_UTXOS, STORE_TRANSACTIONS, STORE_WALLET_STATE],
                    "readwrite",
                );
                const vtxosStore = transaction.objectStore(STORE_VTXOS);
                const utxosStore = transaction.objectStore(STORE_UTXOS);
                const transactionsStore = transaction.objectStore(STORE_TRANSACTIONS);
                const walletStateStore = transaction.objectStore(STORE_WALLET_STATE);

                const requests = [
                    vtxosStore.clear(),
                    utxosStore.clear(),
                    transactionsStore.clear(),
                    walletStateStore.clear(),
                ];

                let completed = 0;
                const checkComplete = () => {
                    completed++;
                    if (completed === requests.length) {
                        resolve();
                    }
                };

                requests.forEach((request) => {
                    request.onsuccess = checkComplete;
                    request.onerror = () => reject(request.error);
                });
            });
        } catch (error) {
            console.error("Failed to clear wallet data:", error);
            throw error;
        }
    }

    async [Symbol.asyncDispose](): Promise<void> {
        if (!this.db) return;
        await closeDatabase(this.dbName);
        this.db = null;
    }

    async getVtxos(address: string): Promise<ExtendedVirtualCoin[]> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_VTXOS], "readonly");
                const store = transaction.objectStore(STORE_VTXOS);
                const index = store.index("address");
                const request: IDBRequest<(SerializedVtxo & { address: string })[]> =
                    index.getAll(address);

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const results = request.result || [];
                    // Wrap `.map` in try/catch so a bad row (e.g. a legacy
                    // VTXO whose address can't be decoded during backfill)
                    // rejects the promise instead of silently throwing
                    // inside the IDB event handler, which would otherwise
                    // hang the caller.
                    try {
                        resolve(results.map(deserializeVtxoWithBackfill));
                    } catch (err) {
                        reject(err);
                    }
                };
            });
        } catch (error) {
            console.error(`Failed to get VTXOs for address ${address}:`, error);
            return [];
        }
    }

    async saveVtxos(address: string, vtxos: ExtendedVirtualCoin[]): Promise<void> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_VTXOS], "readwrite");
                const store = transaction.objectStore(STORE_VTXOS);

                const promises = vtxos.map((vtxo) => {
                    return new Promise<void>((resolveItem, rejectItem) => {
                        const serialized: SerializedVtxo = serializeVtxo(vtxo);
                        const item = {
                            address,
                            ...serialized,
                        };
                        const request = store.put(item);

                        request.onerror = () => rejectItem(request.error);
                        request.onsuccess = () => resolveItem();
                    });
                });

                Promise.all(promises)
                    .then(() => resolve())
                    .catch(reject);

                transaction.onerror = () => reject(transaction.error);
            });
        } catch (error) {
            console.error(`Failed to save VTXOs for address ${address}:`, error);
            throw error;
        }
    }

    async deleteVtxos(address: string): Promise<void> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_VTXOS], "readwrite");
                const store = transaction.objectStore(STORE_VTXOS);
                const index = store.index("address");
                const request = index.openCursor(IDBKeyRange.only(address));

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };
            });
        } catch (error) {
            console.error(`Failed to clear VTXOs for address ${address}:`, error);
            throw error;
        }
    }

    async getVtxosForScript(script: string): Promise<ExtendedVirtualCoin[]> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_VTXOS], "readonly");
                const store = transaction.objectStore(STORE_VTXOS);
                const index = store.index("script");
                const request: IDBRequest<(SerializedVtxo & { address: string })[]> =
                    index.getAll(script);

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const results = request.result || [];
                    try {
                        // Defensive filter: only rows whose script matches.
                        const matching = results.filter((r) => r.script === script);

                        // Dedup same outpoint rows across address buckets.
                        // Work on raw rows so the address field is available
                        // for the canonicality tiebreaker.
                        const byOutpoint = new Map<string, SerializedVtxo & { address: string }>();
                        for (const row of matching) {
                            const outpoint = `${row.txid}:${row.vout}`;
                            const existing = byOutpoint.get(outpoint);
                            if (!existing) {
                                byOutpoint.set(outpoint, row);
                                continue;
                            }
                            if (shouldReplaceVtxo(existing, row)) {
                                byOutpoint.set(outpoint, row);
                            }
                        }
                        resolve(Array.from(byOutpoint.values()).map(deserializeVtxoWithBackfill));
                    } catch (err) {
                        reject(err);
                    }
                };
            });
        } catch (error) {
            console.error(`Failed to get VTXOs for script ${script}:`, error);
            throw error;
        }
    }

    async saveVtxosForScript(key: VtxoRepositoryKey, vtxos: ExtendedVirtualCoin[]): Promise<void> {
        if (!key.address) {
            throw new Error("IndexedDBWalletRepository requires an address");
        }
        for (const vtxo of vtxos) {
            if (!isVtxoForScript(vtxo, key.script)) {
                throw new Error(
                    `VTXO ${vtxo.txid}:${vtxo.vout} script mismatch: expected ${key.script}, got ${vtxo.script}`,
                );
            }
        }
        return this.saveVtxos(key.address, vtxos);
    }

    async deleteVtxosForScript(script: string): Promise<void> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_VTXOS], "readwrite");
                const store = transaction.objectStore(STORE_VTXOS);
                const index = store.index("script");
                const request = index.openCursor(IDBKeyRange.only(script));

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };
            });
        } catch (error) {
            console.error(`Failed to clear VTXOs for script ${script}:`, error);
            throw error;
        }
    }

    async getUtxos(address: string): Promise<ExtendedCoin[]> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_UTXOS], "readonly");
                const store = transaction.objectStore(STORE_UTXOS);
                const index = store.index("address");
                const request = index.getAll(address);

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const results = request.result || [];
                    const utxos = results.map(deserializeUtxo);
                    resolve(utxos);
                };
            });
        } catch (error) {
            console.error(`Failed to get UTXOs for address ${address}:`, error);
            return [];
        }
    }

    async saveUtxos(address: string, utxos: ExtendedCoin[]): Promise<void> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_UTXOS], "readwrite");
                const store = transaction.objectStore(STORE_UTXOS);

                const promises = utxos.map((utxo) => {
                    return new Promise<void>((resolveItem, rejectItem) => {
                        const serialized = serializeUtxo(utxo);
                        const item = {
                            address,
                            ...serialized,
                        };
                        const request = store.put(item);

                        request.onerror = () => rejectItem(request.error);
                        request.onsuccess = () => resolveItem();
                    });
                });

                Promise.all(promises)
                    .then(() => resolve())
                    .catch(reject);

                transaction.onerror = () => reject(transaction.error);
            });
        } catch (error) {
            console.error(`Failed to save UTXOs for address ${address}:`, error);
            throw error;
        }
    }

    async deleteUtxos(address: string): Promise<void> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_UTXOS], "readwrite");
                const store = transaction.objectStore(STORE_UTXOS);
                const index = store.index("address");
                const request = index.openCursor(IDBKeyRange.only(address));

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };
            });
        } catch (error) {
            console.error(`Failed to clear UTXOs for address ${address}:`, error);
            throw error;
        }
    }

    async getTransactionHistory(address: string): Promise<ArkTransaction[]> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_TRANSACTIONS], "readonly");
                const store = transaction.objectStore(STORE_TRANSACTIONS);
                const index = store.index("address");
                const request = index.getAll(address);

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const results = request.result || [];
                    resolve(results.sort((a, b) => a.createdAt - b.createdAt));
                };
            });
        } catch (error) {
            console.error(`Failed to get transaction history for address ${address}:`, error);
            return [];
        }
    }

    async saveTransactions(address: string, txs: ArkTransaction[]): Promise<void> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_TRANSACTIONS], "readwrite");
                const store = transaction.objectStore(STORE_TRANSACTIONS);

                // Queue all put operations
                txs.forEach((tx) => {
                    const item = {
                        address,
                        ...tx,
                        keyBoardingTxid: tx.key.boardingTxid,
                        keyCommitmentTxid: tx.key.commitmentTxid,
                        keyArkTxid: tx.key.arkTxid,
                    };
                    store.put(item);
                });

                // Handle transaction completion
                transaction.oncomplete = () => resolve();
                transaction.onerror = () => reject(transaction.error);
                transaction.onabort = () => reject(new Error("Transaction aborted"));
            });
        } catch (error) {
            console.error(`Failed to save transactions for address ${address}:`, error);
            throw error;
        }
    }

    async deleteTransactions(address: string): Promise<void> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_TRANSACTIONS], "readwrite");
                const store = transaction.objectStore(STORE_TRANSACTIONS);
                const index = store.index("address");
                const request = index.openCursor(IDBKeyRange.only(address));

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };
            });
        } catch (error) {
            console.error(`Failed to clear transactions for address ${address}:`, error);
            throw error;
        }
    }

    async getWalletState(): Promise<WalletState | null> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_WALLET_STATE], "readonly");
                const store = transaction.objectStore(STORE_WALLET_STATE);
                const request = store.get("state");

                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const result = request.result;
                    if (result && result.data) {
                        resolve(result.data);
                    } else {
                        resolve(null);
                    }
                };
            });
        } catch (error) {
            console.error("Failed to get wallet state:", error);
            return null;
        }
    }

    async saveWalletState(state: WalletState): Promise<void> {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([STORE_WALLET_STATE], "readwrite");
                const store = transaction.objectStore(STORE_WALLET_STATE);
                const item = {
                    key: "state",
                    data: state,
                };
                const request = store.put(item);

                request.onerror = () => reject(request.error);
                request.onsuccess = () => resolve();
            });
        } catch (error) {
            console.error("Failed to save wallet state:", error);
            throw error;
        }
    }

    private async getDB(): Promise<IDBDatabase> {
        if (this.db) return this.db;
        this.db = await openDatabase(this.dbName, DB_VERSION, initDatabase);
        return this.db;
    }
}

// Post-migration every row has `script`, but the backfill is idempotent: if a
// legacy row is ever read before the upgrade-path completes, derive `script`
// from `address` the same way the indexer would have populated it.
function deserializeVtxoWithBackfill(o: SerializedVtxo & { address: string }): ExtendedVirtualCoin {
    if (!o.script) {
        o = { ...o, script: scriptFromArkAddress(o.address) };
    }
    return deserializeVtxo(o);
}

type RawVtxoRow = SerializedVtxo & { address: string };

function isCanonicalRow(row: RawVtxoRow): boolean {
    try {
        return scriptFromArkAddress(row.address) === row.script;
    } catch {
        return false;
    }
}

function shouldReplaceVtxo(existing: RawVtxoRow, incoming: RawVtxoRow): boolean {
    const existingCanonical = isCanonicalRow(existing);
    const incomingCanonical = isCanonicalRow(incoming);

    if (incomingCanonical && !existingCanonical) return true;
    if (existingCanonical && !incomingCanonical) return false;

    // Tie on canonicality, check lifecycle completeness
    const existingWeight = getLifecycleWeight(existing);
    const incomingWeight = getLifecycleWeight(incoming);

    if (incomingWeight > existingWeight) return true;
    if (existingWeight > incomingWeight) return false;

    // Tie on weight, stable sort by address
    return incoming.address < existing.address;
}

function getLifecycleWeight(v: RawVtxoRow): number {
    let weight = 0;
    if (v.isSpent !== undefined) weight += 1;
    if (v.spentBy) weight += 2;
    if (v.settledBy) weight += 2;
    if (v.arkTxId) weight += 2;
    return weight;
}
